
import { useState, useEffect } from 'react';
import logger from './../../../utils/logger.js';


const useMediaStreams = (videoRef, isMobile) => {
  const [isSpeaking, setSpeaking] = useState(false);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [isPausedByTab, setIsPausedByTab] = useState(false);

  useEffect(() => {
    let stream = null;
    let audioAnalyzerRunning = false;
    
    const getMedia = async () => {
      try {
        const constraints = {
          video: {
            width: { ideal: isMobile ? 640 : 1280 },
            height: { ideal: isMobile ? 480 : 720 },
            facingMode: 'user',
            aspectRatio: { ideal: 1.333333 },
          },
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 48000,
            channelCount: 1
          }
        };

        stream = await navigator.mediaDevices.getUserMedia(constraints);

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.muted = true; // Always mute the local video preview
          playVideo();
        }

        // Audio analysis setup for speech detection
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const audioSource = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();

        // Prevent audio feedback loop
        audioSource.connect(analyser);
        // DO NOT connect the analyser to audioContext.destination

        analyser.fftSize = 2048;
        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const detectSpeech = () => {
          analyser.getByteFrequencyData(dataArray);
          // Check if audio level is above threshold
          const isSpeaking = dataArray.some(value => value > 128);
          setSpeaking(isSpeaking);
          requestAnimationFrame(detectSpeech);
        };

        detectSpeech();

      } catch (error) {
        logger.error("Error accessing media devices.", error);
      }
    };

    getMedia();
    
    // Cleanup function to stop all tracks when component unmounts
    // Function to safely play the video without interrupting other media elements
    const playVideo = async () => {
      if (!videoRef.current || !videoRef.current.srcObject) return;

      try {
        // Only attempt to play the video if it's paused and not paused by tab switching
        if (videoRef.current.paused && !isPausedByTab) {
          // Use a timeout to allow any previous play requests to settle
          setTimeout(async () => {
            try {
              // Store play promise to track its state
              const playPromise = videoRef.current.play();
              
              // If the promise exists, handle potential errors
              if (playPromise !== undefined) {
                playPromise.catch(err => {
                  // Only log non-abort errors since AbortError is expected during tab switches
                  if (err.name !== 'AbortError') {
                    logger.error('Error playing video:', err);
                  }
                });
              }
            } catch (err) {
              if (err.name !== 'AbortError') {
                logger.error('Error in delayed video play:', err);
              }
            }
          }, 200);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          logger.error('Error in playVideo function:', err);
        }
      }
    };
    
    // Handle tab switching with less disruption
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Mark that we're in background mode - but don't pause video explicitly
        // This prevents conflict with Daily.co iframe
        setIsPausedByTab(true);
      } else {
        // Tab is visible again, clear the paused by tab flag
        setIsPausedByTab(false);
        
        // Only attempt to resume our video if it's not a Daily call in control
        if (!document.querySelector('iframe[title="Daily.co embedded video"]')) {
          // Wait a bit longer before resuming to avoid conflicts
          setTimeout(() => {
            playVideo();
          }, 500);
        }
      }
    };
    
    // Add visibility change event listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      // Remove event listener
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Stop audio analyzer if running
      audioAnalyzerRunning = false;
      
      // Clean up and stop all tracks when component unmounts
      if (videoRef.current && videoRef.current.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => {
          track.stop();
          track.enabled = false;
        });
      }
    };
  }, [videoRef, isMobile]);

  return { isSpeaking, cameraEnabled, audioEnabled };
};

export default useMediaStreams;